import { apiClient } from '@/common/lib/api-client';
import { parseApiError } from '@/common/utils';

/**
 * Retrieve the current logged-in user profile details.
 * GET /v1/accounts/profile/
 */
export async function getUserProfile() {
  const { data } = await apiClient.get('/v1/accounts/profile/');
  return data;
}

/**
 * Update the user profile (name, email, phone, profile image).
 * PATCH /v1/accounts/profile/update/
 */
export async function updateUserProfile({
  firstName,
  lastName,
  email,
  phone,
  profileImage,
}) {
  const formData = new FormData();
  if (firstName !== undefined) formData.append('first_name', firstName);
  if (lastName !== undefined) formData.append('last_name', lastName);
  if (email !== undefined) formData.append('email', email);
  if (phone !== undefined) formData.append('phone', phone);
  if (profileImage instanceof File)
    formData.append('profile_picture', profileImage);

  const { data } = await apiClient.patch(
    '/v1/accounts/profile/update/',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

/**
 * Retrieve company details.
 * GET /v1/companies/{companyId}/
 */
export async function getCompanyDetails(companyId) {
  const { data } = await apiClient.get(`/v1/companies/${companyId}/`);
  return data;
}

/**
 * Create a new WhatsApp contact/client
 * POST /v1/whatsapp/companies/{company_id}/clients/
 */
export async function createContact({ companyId, payload }) {
  const { data } = await apiClient.post(
    `/v1/whatsapp/companies/${companyId}/clients/`,
    payload,
  );
  return data;
}

/**
 * Retrieve the list of WhatsApp contacts/clients
 * GET /v1/whatsapp/companies/{company_id}/clients/
 */
export async function getContacts(companyId, params = {}) {
  const { data } = await apiClient.get(
    `/v1/whatsapp/companies/${companyId}/clients/`,
    { params },
  );
  return data;
}

/**
 * Export WhatsApp contacts/clients
 * GET /v1/whatsapp/companies/{company_id}/clients/export/
 */
export async function exportWhatsAppClients(companyId, params = {}) {
  const { data } = await apiClient.get(
    `/v1/whatsapp/companies/${companyId}/clients/export/`,
    { params },
  );
  return data;
}

/**
 * Retrieve a single WhatsApp contact/client details
 * GET /v1/whatsapp/companies/{company_id}/clients/{clientId}/
 */
export async function getContact({ companyId, clientId }) {
  const { data } = await apiClient.get(
    `/v1/whatsapp/companies/${companyId}/clients/${clientId}/`,
  );
  return data;
}

/**
 * Update an existing WhatsApp contact/client
 * PATCH /v1/whatsapp/companies/{company_id}/clients/{clientId}/
 */
export async function updateContact({ companyId, clientId, payload }) {
  const { data } = await apiClient.patch(
    `/v1/whatsapp/companies/${companyId}/clients/${clientId}/`,
    payload,
  );
  return data;
}

/**
 * Delete a WhatsApp contact/client
 * DELETE /v1/whatsapp/companies/{company_id}/clients/{clientId}/
 */
export async function deleteContact({ companyId, clientId }) {
  const { data } = await apiClient.delete(
    `/v1/whatsapp/companies/${companyId}/clients/${clientId}/`,
  );
  return data;
}

/**
 * Convert a WhatsApp Client to a Lead
 * POST /v1/whatsapp/companies/{company_id}/clients/{client_id}/convert-to-lead/
 */
export async function convertContactToLead({ companyId, clientId }) {
  const { data } = await apiClient.post(
    `/v1/whatsapp/companies/${companyId}/clients/${clientId}/convert-to-lead/`,
  );
  return data;
}

/**
 * Count clients a bulk action would hit.
 * POST /v1/whatsapp/companies/{company_id}/clients/bulk-actions/preview/
 */
export async function previewClientBulkAction({ companyId, selection }) {
  const { data } = await apiClient.post(
    `/v1/whatsapp/companies/${companyId}/clients/bulk-actions/preview/`,
    { selection },
  );
  return data;
}

/**
 * Delete or replace customer types for a client selection.
 * POST /v1/whatsapp/companies/{company_id}/clients/bulk-actions/
 */
export async function runClientBulkAction({
  companyId,
  action,
  selection,
  payload,
}) {
  const body = { action, selection };
  if (payload !== undefined) body.payload = payload;
  const { data } = await apiClient.post(
    `/v1/whatsapp/companies/${companyId}/clients/bulk-actions/`,
    body,
  );
  return data;
}

/**
 * Retrieve the list of Customer Types
 * GET /v1/whatsapp/companies/{company_id}/customer-types/
 */
export async function getCustomerTypes(companyId, params = {}) {
  const { data } = await apiClient.get(
    `/v1/whatsapp/companies/${companyId}/customer-types/`,
    { params },
  );
  return data;
}

/**
 * Create a new Customer Type
 * POST /v1/whatsapp/companies/{company_id}/customer-types/
 */
export async function createCustomerType({ companyId, payload }) {
  const { data } = await apiClient.post(
    `/v1/whatsapp/companies/${companyId}/customer-types/`,
    payload,
  );
  return data;
}

/**
 * Retrieve details of a specific Customer Type
 * GET /v1/whatsapp/companies/{company_id}/customer-types/{type_id}/
 */
export async function getCustomerTypeById({ companyId, typeId }) {
  const { data } = await apiClient.get(
    `/v1/whatsapp/companies/${companyId}/customer-types/${typeId}/`,
  );
  return data;
}

/**
 * Update an existing Customer Type
 * PATCH /v1/whatsapp/companies/{company_id}/customer-types/{type_id}/
 */
export async function updateCustomerType({ companyId, typeId, payload }) {
  const { data } = await apiClient.patch(
    `/v1/whatsapp/companies/${companyId}/customer-types/${typeId}/`,
    payload,
  );
  return data;
}

/**
 * Delete a Customer Type
 * DELETE /v1/whatsapp/companies/{company_id}/customer-types/{type_id}/
 */
export async function deleteCustomerType({ companyId, typeId }) {
  const { data } = await apiClient.delete(
    `/v1/whatsapp/companies/${companyId}/customer-types/${typeId}/`,
  );
  return data;
}

/** Local DB list uses `templates`; older callers read `results`. */
function normalizeTemplateList(data) {
  if (!data || Array.isArray(data) || Array.isArray(data.results)) return data;
  if (Array.isArray(data.templates))
    return { ...data, results: data.templates };
  return data;
}

/**
 * Retrieve WhatsApp templates for a company.
 * GET /v1/whatsapp/companies/{companyId}/templates/
 * @param {string|number} companyId
 * @param {{ search?: string, name?: string, category?: string, is_archived?: boolean|string }} [params]
 */
export async function getWhatsAppTemplates(
  companyId,
  params = {},
  config = {},
) {
  const query = {};
  if (params.search) query.search = params.search;
  if (params.name) query.name = params.name;
  if (params.category && params.category !== 'ALL')
    query.category = params.category;
  if (params.is_archived != null) query.is_archived = params.is_archived;

  const { data } = await apiClient.get(
    `/v1/whatsapp/companies/${companyId}/templates/`,
    { params: query, ...config },
  );
  return normalizeTemplateList(data);
}

/**
 * Pull live templates from Meta into the local DB.
 * POST /v1/whatsapp/companies/{companyId}/templates/sync/
 */
export async function syncWhatsAppTemplates(companyId) {
  const { data } = await apiClient.post(
    `/v1/whatsapp/companies/${companyId}/templates/sync/`,
  );
  return data;
}

/**
 * Create a new WhatsApp template for a company.
 * POST /v1/whatsapp/companies/{companyId}/templates/
 */
export async function createWhatsAppTemplate({ companyId, payload }) {
  const { data } = await apiClient.post(
    `/v1/whatsapp/companies/${companyId}/templates/`,
    payload,
  );
  return data;
}

/**
 * Validate a WhatsApp template payload before create.
 * POST /v1/whatsapp/companies/{companyId}/templates/validate/
 */
export async function validateWhatsAppTemplate({ companyId, payload }) {
  const { data } = await apiClient.post(
    `/v1/whatsapp/companies/${companyId}/templates/validate/`,
    payload,
  );
  return data;
}

/**
 * Retrieve archived WhatsApp templates for a company.
 * GET /v1/whatsapp/companies/{companyId}/templates/?is_archived=true
 * @param {string|number} companyId
 * @param {{ search?: string, category?: string }} [params]
 */
export async function getArchivedWhatsAppTemplates(
  companyId,
  params = {},
  config = {},
) {
  const query = { is_archived: true };
  if (params.search) query.search = params.search;
  if (params.category && params.category !== 'ALL')
    query.category = params.category;

  const { data } = await apiClient.get(
    `/v1/whatsapp/companies/${companyId}/templates/`,
    { params: query, ...config },
  );
  return normalizeTemplateList(data);
}

/**
 * Archive or unarchive a WhatsApp template.
 * POST /v1/whatsapp/companies/{companyId}/templates/{templateName}/archive/
 * @param {boolean} isArchived - true to archive, false to unarchive
 */
export async function setWhatsAppTemplateArchived({
  companyId,
  templateName,
  isArchived,
}) {
  const { data } = await apiClient.post(
    `/v1/whatsapp/companies/${companyId}/templates/${templateName}/archive/`,
    { is_archived: isArchived },
  );
  return data;
}

/**
 * Import contacts from a file (CSV/Excel)
 * POST /v1/whatsapp/companies/{company_id}/clients/import/
 */
export async function importContacts({ companyId, file }) {
  const formData = new FormData();
  formData.append('file', file);

  const { data } = await apiClient.post(
    `/v1/whatsapp/companies/${companyId}/clients/import/`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
  return data;
}

/**
 * Create bulk WhatsApp campaign (send_now | schedule_once | interval)
 * POST /v1/whatsapp/companies/{company_id}/messages/bulk/
 *
 * Payload must include `sending_mode`. Mode-specific fields:
 * - send_now: immediate send
 * - schedule_once: `scheduled_date_time`, optional `stop_on_reply`
 * - interval: `interval_value`, `interval_unit`, `start_date`,
 *   optional `end_date`, `max_attempts`, `stop_on_reply`
 */
export async function sendBulkMessages({ companyId, payload }) {
  const { data } = await apiClient.post(
    `/v1/whatsapp/companies/${companyId}/messages/bulk/`,
    payload,
  );
  return data;
}

/**
 * Direct WhatsApp Template Send (single or list of numbers/clients)
 * POST /v1/whatsapp/companies/{company_id}/messages/send-template/
 */
export async function sendDirectTemplateMessage({ companyId, payload }) {
  const { data } = await apiClient.post(
    `/v1/whatsapp/companies/${companyId}/messages/send-template/`,
    payload,
  );
  return data;
}

/**
 * Replace recipients on a scheduled bulk campaign.
 * PATCH /v1/whatsapp/companies/{company_id}/messages/bulk/{campaign_id}/
 * `selection` replaces the list. `customer_ids` replaces it only when selection is absent.
 */
export async function updateBulkCampaign({ companyId, campaignId, payload }) {
  const { data } = await apiClient.patch(
    `/v1/whatsapp/companies/${companyId}/messages/bulk/${campaignId}/`,
    payload,
  );
  return data;
}

/**
 * Retrieve bulk WhatsApp message campaigns
 * GET /v1/whatsapp/companies/{companyId}/messages/bulk/
 */
export async function getBulkCampaigns(companyId, params = {}, config = {}) {
  const { data } = await apiClient.get(
    `/v1/whatsapp/companies/${companyId}/messages/bulk/`,
    { params, ...config },
  );
  return data;
}

/**
 * Retrieve recent campaigns for the dashboard.
 * GET /v1/dashboard/companies/{companyId}/recent-campaigns/
 */
export async function getRecentCampaigns(companyId, params = {}) {
  const { data } = await apiClient.get(
    `/v1/dashboard/companies/${companyId}/recent-campaigns/`,
    { params },
  );
  return data;
}

/**
 * Retrieve details for a specific bulk campaign
 * GET /v1/whatsapp/companies/{companyId}/messages/bulk/{campaignId}/
 */
export async function getCampaignDetails({ companyId, campaignId }) {
  const { data } = await apiClient.get(
    `/v1/whatsapp/companies/${companyId}/messages/bulk/${campaignId}/`,
  );
  return data;
}

/**
 * Retrieve delivery logs for a specific bulk campaign
 * GET /v1/whatsapp/companies/{companyId}/messages/bulk/{campaignId}/logs/
 */
export async function getCampaignLogs({ companyId, campaignId, params = {} }) {
  const { data } = await apiClient.get(
    `/v1/whatsapp/companies/${companyId}/messages/bulk/${campaignId}/logs/`,
    { params },
  );
  return data;
}

/**
 * Retry failed messages in a bulk campaign
 * POST /v1/whatsapp/companies/{companyId}/messages/bulk/{campaignId}/retry/
 */
export async function retryBulkCampaignMessages({ companyId, campaignId }) {
  const { data } = await apiClient.post(
    `/v1/whatsapp/companies/${companyId}/messages/bulk/${campaignId}/retry/`,
  );
  return data;
}

/**
 * Stop/Cancel a scheduled or interval bulk campaign
 * POST /v1/whatsapp/companies/{companyId}/messages/bulk/{campaignId}/stop/
 */
export async function stopBulkCampaign({ companyId, campaignId }) {
  const { data } = await apiClient.post(
    `/v1/whatsapp/companies/${companyId}/messages/bulk/${campaignId}/stop/`,
  );
  return data;
}

/**
 * Pause an interval/recurring bulk campaign
 * POST /v1/whatsapp/companies/{companyId}/messages/bulk/{campaignId}/pause/
 */
export async function pauseBulkCampaign({ companyId, campaignId }) {
  const { data } = await apiClient.post(
    `/v1/whatsapp/companies/${companyId}/messages/bulk/${campaignId}/pause/`,
    {},
  );
  return data;
}

/**
 * Resume a paused bulk campaign
 * POST /v1/whatsapp/companies/{companyId}/messages/bulk/{campaignId}/resume/
 */
export async function resumeBulkCampaign({ companyId, campaignId }) {
  const { data } = await apiClient.post(
    `/v1/whatsapp/companies/${companyId}/messages/bulk/${campaignId}/resume/`,
    {},
  );
  return data;
}

/**
 * Reschedule a one-time scheduled bulk campaign
 * POST /v1/whatsapp/companies/{companyId}/messages/bulk/{campaignId}/reschedule/
 * Body: { new_date_time: ISO string }
 */
export async function rescheduleBulkCampaign({
  companyId,
  campaignId,
  newDateTime,
}) {
  const { data } = await apiClient.post(
    `/v1/whatsapp/companies/${companyId}/messages/bulk/${campaignId}/reschedule/`,
    { new_date_time: newDateTime },
  );
  return data;
}

/**
 * Retrieve dashboard statistics for a company.
 * GET /v1/dashboard/companies/{companyId}/stats/
 */
export async function getDashboardStats(companyId) {
  const { data } = await apiClient.get(
    `/v1/dashboard/companies/${companyId}/stats/`,
  );
  return data;
}

/**
 * Retrieve notifications for a company.
 * GET /v1/notifications/companies/{companyId}/
 */
export async function getNotifications(companyId, params = {}) {
  const { data } = await apiClient.get(
    `/v1/notifications/companies/${companyId}/`,
    { params },
  );
  return data;
}

/**
 * Mark a notification as read.
 * POST /v1/notifications/companies/{companyId}/{notificationId}/mark-read/
 */
export async function markNotificationAsRead({ companyId, notificationId }) {
  const { data } = await apiClient.post(
    `/v1/notifications/companies/${companyId}/${notificationId}/mark-read/`,
  );
  return data;
}

/**
 * Mark multiple notifications as read.
 * POST /v1/notifications/companies/{companyId}/mark-read-bulk/
 */
export async function markNotificationsAsReadBulk({
  companyId,
  notification_ids = [],
  mark_all = false,
}) {
  const { data } = await apiClient.post(
    `/v1/notifications/companies/${companyId}/mark-read-bulk/`,
    { notification_ids, mark_all },
  );
  return data;
}

/**
 * Update company details.
 * PATCH /v1/companies/{companyId}/update/
 */
export async function updateCompanyDetails({ companyId, payload }) {
  const { data } = await apiClient.patch(
    `/v1/companies/${companyId}/update/`,
    payload,
  );
  return data;
}

/**
 * Retrieve company preferences (country, currency, language).
 * GET /v1/companies/{companyId}/preferences/
 *
 * @param {string|number} companyId
 * @returns {Promise<import('../types').CompanyPreferences>}
 */
export async function getCompanyPreferences(companyId) {
  const { data } = await apiClient.get(
    `/v1/companies/${companyId}/preferences/`,
  );
  return data;
}

/**
 * Update company preferences.
 * PATCH /v1/companies/{companyId}/preferences/
 *
 * @param {string|number} companyId
 * @param {import('../types').CompanyPreferences} payload
 * @returns {Promise<import('../types').CompanyPreferences>}
 */
export async function updateCompanyPreferences(companyId, payload) {
  const { data } = await apiClient.patch(
    `/v1/companies/${companyId}/preferences/`,
    payload,
  );
  return data;
}

/**
 * Retrieve available company currencies.
 * GET /v1/companies/currencies/
 *
 * @param {{ search?: string, page?: string|number }} [params]
 * @returns {Promise<unknown>}
 */
export async function getCompanyCurrencies(params = {}) {
  const query = {};
  if (params.search) query.search = params.search;
  if (params.page) query.page = params.page;
  const { data } = await apiClient.get('/v1/companies/currencies/', {
    params: query,
  });
  return data;
}

/**
 * Retrieve integrations status for a company
 * GET /v1/integrations/companies/{companyId}/
 */
export async function getCompanyIntegrations(companyId) {
  const { data } = await apiClient.get(
    `/v1/integrations/companies/${companyId}/`,
  );
  return data;
}

/**
 * Retrieve integration health status
 * GET /v1/integrations/companies/{companyId}/{companyIntegrationId}/health/
 */
export async function getIntegrationHealth({
  companyId,
  companyIntegrationId,
}) {
  const { data } = await apiClient.get(
    `/v1/integrations/companies/${companyId}/${companyIntegrationId}/health/`,
  );
  return data;
}

/**
 * Retrieve integration health status history
 * GET /v1/integrations/companies/{companyId}/{companyIntegrationId}/health-history/
 */
export async function getIntegrationHealthHistory({
  companyId,
  companyIntegrationId,
  params = {},
}) {
  const { data } = await apiClient.get(
    `/v1/integrations/companies/${companyId}/${companyIntegrationId}/health-history/`,
    { params },
  );
  return data;
}

/**
 * Retrieve reconnect requests list for a company
 * GET /v1/integrations/companies/{companyId}/reconnect-requests/
 */
export async function getReconnectRequests(companyId, params = {}) {
  const { data } = await apiClient.get(
    `/v1/integrations/companies/${companyId}/reconnect-requests/`,
    { params },
  );
  return data;
}

/**
 * @typedef {Object} CompanyTag
 * @property {number} id
 * @property {string} name
 * @property {string} color
 */

/**
 * Retrieve the list of employees for a company.
 * GET /v1/leads/companies/{company_id}/employees/
 * Each row includes `tags: CompanyTag[]`. Treat missing tags as [].
 */
export async function getCompanyEmployees(companyId, params = {}) {
  const { data } = await apiClient.get(
    `/v1/leads/companies/${companyId}/employees/`,
    { params },
  );
  return data;
}

/**
 * GET /v1/leads/companies/{company_id}/employees/{employee_id}/
 * Includes `tags: CompanyTag[]`. Treat missing tags as [].
 */
export async function getEmployeeDetails(companyId, employeeId) {
  const { data } = await apiClient.get(
    `/v1/leads/companies/${companyId}/employees/${employeeId}/`,
  );
  return data;
}

/**
 * Create a new reconnect request for a company integration
 * POST /v1/integrations/companies/{companyId}/{companyIntegrationId}/reconnect-request/
 */
export async function createReconnectRequest({
  companyId,
  companyIntegrationId,
  payload,
}) {
  const { data } = await apiClient.post(
    `/v1/integrations/companies/${companyId}/${companyIntegrationId}/reconnect-request/`,
    payload,
  );
  return data;
}

/**
 * Create a new employee for a company.
 * POST /v1/leads/companies/{company_id}/employees/create/
 */
export async function createCompanyEmployee({ companyId, payload }) {
  const { data } = await apiClient.post(
    `/v1/leads/companies/${companyId}/employees/create/`,
    payload,
  );
  return data;
}

/**
 * Update an existing employee for a company.
 * PUT /v1/leads/companies/{company_id}/employees/{employee_id}/update/
 */
export async function updateCompanyEmployee({
  companyId,
  employeeId,
  payload,
}) {
  const { data } = await apiClient.put(
    `/v1/leads/companies/${companyId}/employees/${employeeId}/update/`,
    payload,
  );
  return data;
}

/**
 * Delete an employee for a company.
 * DELETE /v1/leads/companies/{company_id}/employees/{employee_id}/erase/
 */
export async function deleteCompanyEmployee({ companyId, employeeId }) {
  const { data } = await apiClient.delete(
    `/v1/leads/companies/${companyId}/employees/${employeeId}/erase/`,
  );
  return data;
}

/**
 * Toggle auto-assign ON/OFF for an individual employee.
 * PATCH /v1/leads/companies/{company_id}/employees/{employee_id}/auto-assign/
 */
export async function toggleEmployeeAutoAssign({
  companyId,
  employeeId,
  payload,
}) {
  const { data } = await apiClient.patch(
    `/v1/leads/companies/${companyId}/employees/${employeeId}/auto-assign/`,
    payload,
  );
  return data;
}

/**
 * Retrieve the list of customer feedback
 * GET /v1/feedback/companies/{companyId}/
 */
export async function getFeedbackList(companyId, params = {}) {
  const { data } = await apiClient.get(`/v1/feedback/companies/${companyId}/`, {
    params,
  });
  return data;
}

/**
 * Retrieve details for a specific feedback
 * GET /v1/feedback/companies/{companyId}/{feedbackId}/
 */
export async function getFeedbackDetails({ companyId, feedbackId }) {
  const { data } = await apiClient.get(
    `/v1/feedback/companies/${companyId}/${feedbackId}/`,
  );
  return data;
}

/**
 * Submit new customer feedback
 * POST /v1/feedback/companies/{companyId}/submit/
 */
export async function submitFeedback({ companyId, payload }) {
  const { data } = await apiClient.post(
    `/v1/feedback/companies/${companyId}/submit/`,
    payload,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

/**
 * Retrieve billing reports for campaigns
 * GET /v1/whatsapp/companies/{companyId}/reports/cost/
 */
export async function getCampaignCost(companyId, params = {}, config = {}) {
  const { data } = await apiClient.get(
    `/v1/whatsapp/companies/${companyId}/reports/cost/`,
    { params, ...config },
  );
  return data;
}

/**
 * Retrieve CRM dashboard statistics for a company.
 * GET /v1/leads/companies/{companyId}/crm-dashboard/
 */
export async function getCRMDashboardStats(companyId) {
  const { data } = await apiClient.get(
    `/v1/leads/companies/${companyId}/crm-dashboard/`,
  );
  return data;
}

/**
 * Retrieve CRM dashboard pipeline statistics for a company.
 * GET /v1/leads/companies/{companyId}/crm-dashboard/pipeline/
 */
export async function getCRMPipelineStats(companyId) {
  const { data } = await apiClient.get(
    `/v1/leads/companies/${companyId}/crm-dashboard/pipeline/`,
  );
  return data;
}

/**
 * Retrieve CRM dashboard sources statistics for a company.
 * GET /v1/leads/companies/{companyId}/crm-dashboard/sources/
 */
export async function getCRMSourcesStats(companyId) {
  const { data } = await apiClient.get(
    `/v1/leads/companies/${companyId}/crm-dashboard/sources/`,
  );
  return data;
}

/**
 * Retrieve CRM dashboard recent leads for a company.
 * GET /v1/leads/companies/{companyId}/crm-dashboard/recent-leads/
 */
export async function getCRMRecentLeads(companyId, params = {}) {
  const { data } = await apiClient.get(
    `/v1/leads/companies/${companyId}/crm-dashboard/recent-leads/`,
    {
      params,
    },
  );
  return data;
}

/**
 * Retrieve CRM dashboard tasks and follow-ups.
 * GET /v1/leads/companies/{companyId}/crm-dashboard/tasks-and-followups/
 */
export async function getCRMTasksAndFollowups(companyId, params = {}) {
  const { data } = await apiClient.get(
    `/v1/leads/companies/${companyId}/crm-dashboard/tasks-and-followups/`,
    {
      params,
    },
  );
  return data;
}

/**
 * Retrieve Business Card Config
 * GET /v1/companies/{companyId}/business-card-config/
 */
export async function getBusinessCardConfig(companyId) {
  const { data } = await apiClient.get(
    `/v1/companies/${companyId}/business-card-config/`,
  );
  return data;
}

/**
 * Update Business Card Config
 * PATCH /v1/companies/{companyId}/business-card-config/
 */
export async function updateBusinessCardConfig({ companyId, payload }) {
  const { data } = await apiClient.patch(
    `/v1/companies/${companyId}/business-card-config/`,
    payload,
  );
  return data;
}

// --- Roles API ---

export async function getRoles(companyId, params = {}) {
  const { data } = await apiClient.get(`/v1/companies/${companyId}/roles/`, {
    params,
  });
  return data;
}

export async function getPermissions(companyId, params = {}) {
  const { data } = await apiClient.get(
    `/v1/companies/${companyId}/permissions/`,
    { params },
  );
  return data;
}

export async function createRole({ companyId, payload }) {
  const { data } = await apiClient.post(
    `/v1/companies/${companyId}/roles/`,
    payload,
  );
  return data;
}

export async function getRoleDetails({ companyId, roleId }) {
  const { data } = await apiClient.get(
    `/v1/companies/${companyId}/roles/${roleId}/`,
  );
  return data;
}

export async function getRoleEmployees({ companyId, roleId, params = {} }) {
  const { data } = await apiClient.get(
    `/v1/companies/${companyId}/roles/${roleId}/employees/`,
    { params },
  );
  return data;
}

export async function updateRole({ companyId, roleId, payload }) {
  const { data } = await apiClient.patch(
    `/v1/companies/${companyId}/roles/${roleId}/`,
    payload,
  );
  return data;
}

export async function deleteRole({ companyId, roleId }) {
  const { data } = await apiClient.delete(
    `/v1/companies/${companyId}/roles/${roleId}/`,
  );
  return data;
}

/**
 * Retrieve the list of active company members with their assigned roles.
 * GET /api/v1/companies/{company_id}/employees/
 */
export async function getCompanyEmployeesWithRoles(companyId, params = {}) {
  const { data } = await apiClient.get(
    `/v1/leads/companies/${companyId}/employees/`,
    { params },
  );
  return data;
}

/**
 * Get WhatsApp company settings & custom messages
 * GET /v1/whatsapp/companies/{company_id}/whatsapp/settings/
 */
export async function getWhatsAppSettings(companyId) {
  const { data } = await apiClient.get(
    `/v1/whatsapp/companies/${companyId}/whatsapp/settings/`,
  );
  return data;
}

/**
 * Assign a role to specific employees.
 * PUT /api/v1/companies/{company_id}/employees/role/
 */
export async function assignRoleToEmployees({ companyId, payload }) {
  const { data } = await apiClient.put(
    `/v1/companies/${companyId}/employees/role/`,
    payload,
  );
  return data;
}

/**
 * Update WhatsApp custom messages
 * PATCH /v1/whatsapp/companies/{company_id}/whatsapp/settings/
 */
export async function updateWhatsAppSettings({ companyId, payload }) {
  const { data } = await apiClient.patch(
    `/v1/whatsapp/companies/${companyId}/whatsapp/settings/`,
    payload,
  );
  return data;
}

/**
 * Connect Instagram integration for a company
 * POST /v1/instagram/companies/{company_id}/connect/
 */
export async function connectInstagramIntegration({ companyId, payload }) {
  const { data } = await apiClient.post(
    `/v1/instagram/companies/${companyId}/connect/`,
    payload,
  );
  return data;
}

/**
 * Retrieve connected Instagram account status & profile info
 * GET /v1/instagram/companies/{company_id}/status/
 */
export async function getInstagramStatus(companyId) {
  const { data } = await apiClient.get(
    `/v1/instagram/companies/${companyId}/status/`,
  );
  return data;
}

/**
 * Disconnect Instagram account for a company
 * POST /v1/instagram/companies/{company_id}/disconnect/
 */
export async function disconnectInstagramAccount(companyId) {
  const { data } = await apiClient.post(
    `/v1/instagram/companies/${companyId}/disconnect/`,
  );
  return data;
}

/**
 * Retrieve Instagram OAuth authorization URL for a company
 * GET /v1/instagram/companies/{company_id}/auth/url/
 */
export async function getInstagramAuthUrl(companyId) {
  const { data } = await apiClient.get(
    `/v1/instagram/companies/${companyId}/auth/url/`,
  );
  return data;
}

/**
 * Retrieve health status for all company integrations
 * GET /v1/integrations/companies/{company_id}/health/
 */
export async function getCompanyIntegrationsHealth(companyId) {
  const { data } = await apiClient.get(
    `/v1/integrations/companies/${companyId}/health/`,
  );
  return data;
}

/**
 * Direct reconnect for Instagram account with OAuth code
 * POST /v1/instagram/companies/{company_id}/reconnect/
 */
export async function reconnectInstagramAccount({ companyId, payload }) {
  const { data } = await apiClient.post(
    `/v1/instagram/companies/${companyId}/reconnect/`,
    payload,
  );
  return data;
}

// ─── Google Workspace OAuth API ───────────────────────────────────────────────

/**
 * Retrieve the Google Workspace OAuth authorization URL for a company.
 * GET /api/v1/google-workspace/companies/{companyId}/auth/
 * @param {string|number} companyId
 * @param {string} [redirectUri] - FE callback URL (optional)
 * @returns {Promise<{ auth_url: string, state: string, scopes: string[] }>}
 */
export async function getGoogleWorkspaceAuthUrl(companyId, redirectUri) {
  const params = redirectUri ? { redirect_uri: redirectUri } : {};
  const { data } = await apiClient.get(
    `/v1/google-workspace/companies/${companyId}/auth/`,
    { params },
  );
  return data;
}

/**
 * Complete the Google Workspace OAuth handshake after Google redirects back.
 * POST /api/v1/google-workspace/companies/{companyId}/auth/complete/
 * @param {string|number} companyId
 * @param {{ code: string, state: string, redirect_uri: string }} payload
 * @returns {Promise<{ company_id: number, status: string, is_connected: boolean, google_email: string }>}
 */
export async function completeGoogleWorkspaceAuth({ companyId, payload }) {
  const { data } = await apiClient.post(
    `/v1/google-workspace/companies/${companyId}/auth/complete/`,
    payload,
  );
  return data;
}

/**
 * Retrieve the current Google Workspace connection status for a company.
 * GET /api/v1/google-workspace/companies/{companyId}/connection/
 * @param {string|number} companyId
 * @returns {Promise<{ status: 'connected'|'disconnected', is_connected: boolean, google_email: string, scopes: string[] }>}
 */
export async function getGoogleWorkspaceStatus(companyId) {
  const { data } = await apiClient.get(
    `/v1/google-workspace/companies/${companyId}/connection/`,
  );
  return data;
}

/**
 * Update Google Workspace connection settings (Drive sync toggles).
 * PATCH /api/v1/google-workspace/companies/{companyId}/connection/
 * @param {string|number} companyId
 * @param {Object} payload
 */
export async function updateGoogleWorkspaceConnection({ companyId, payload }) {
  const { data } = await apiClient.patch(
    `/v1/google-workspace/companies/${companyId}/connection/`,
    payload,
  );
  return data;
}

/**
 * Disconnect (revoke) Google Workspace for a company.
 * POST /api/v1/google-workspace/companies/{companyId}/disconnect/
 * @param {string|number} companyId
 * @returns {Promise<{ is_connected: false, status: 'disconnected' }>}
 */
export async function disconnectGoogleWorkspace(companyId) {
  const { data } = await apiClient.post(
    `/v1/google-workspace/companies/${companyId}/disconnect/`,
  );
  return data;
}

/**
 * GET /v1/windex/companies/{companyId}/status/
 */
export async function getWindexStatus(companyId) {
  const { data } = await apiClient.get(
    `/v1/windex/companies/${companyId}/status/`,
  );
  return data;
}

/**
 * POST /v1/windex/companies/{companyId}/connect/
 * Body is school_code only.
 */
export async function connectWindex(companyId, schoolCode) {
  const { data } = await apiClient.post(
    `/v1/windex/companies/${companyId}/connect/`,
    { school_code: schoolCode },
  );
  return data;
}

/**
 * POST /v1/windex/companies/{companyId}/disconnect/
 */
export async function disconnectWindex(companyId) {
  const { data } = await apiClient.post(
    `/v1/windex/companies/${companyId}/disconnect/`,
  );
  return data;
}

/**
 * POST /v1/windex/companies/{companyId}/contacts/sync/
 * Body is year only. Writes WhatsApp clients; customer type name is the year.
 */
export async function syncWindexContacts(companyId, year) {
  const { data } = await apiClient.post(
    `/v1/windex/companies/${companyId}/contacts/sync/`,
    { year },
  );
  return data;
}

/** GET /v1/windex/companies/{companyId}/contacts/years/ */
export async function getWindexContactYears(companyId) {
  const { data } = await apiClient.get(
    `/v1/windex/companies/${companyId}/contacts/years/`,
  );
  return data;
}

/**
 * GET /v1/windex/companies/{companyId}/pending-fees/
 * Query is year, plus optional class, division, month_number, mobile_no.
 */
export async function getWindexPendingFees(
  companyId,
  { year, mobile, studentClass, division, month },
) {
  const params = { year };
  if (mobile) params.mobile_no = mobile;
  if (studentClass) params.class = studentClass;
  if (division) params.division = division;
  if (month != null && month !== '') params.month_number = month;
  const { data } = await apiClient.get(
    `/v1/windex/companies/${companyId}/pending-fees/`,
    { params },
  );
  return data;
}

/**
 * Retrieve the list of Google Spreadsheets from the company's Google Drive.
 * GET /api/v1/google-workspace/companies/{companyId}/sheets/
 * @param {string|number} companyId
 * @returns {Promise<{ files: Array<{ id: string, name: string, url: string }> }>}
 */
/**
 * Short-lived Google Picker session (token stays in the browser for Picker only).
 * GET /api/v1/google-workspace/companies/{companyId}/picker-session/
 * @returns {Promise<{ access_token: string, expires_at: string, app_id: string, scopes: string[] }>}
 */
export async function getGoogleWorkspacePickerSession(companyId) {
  const { data } = await apiClient.get(
    `/v1/google-workspace/companies/${companyId}/picker-session/`,
  );
  return data;
}

export async function getGoogleWorkspaceSheets(companyId) {
  const { data } = await apiClient.get(
    `/v1/google-workspace/companies/${companyId}/sheets/`,
  );
  return data;
}

/**
 * GET /api/v1/google-workspace/companies/{companyId}/sheets/columns/
 * @param {string|number} companyId
 * @param {{ spreadsheet_id: string, sheet_name?: string }} params
 */
export async function getGoogleWorkspaceSheetColumns(companyId, params) {
  const { data } = await apiClient.get(
    `/v1/google-workspace/companies/${companyId}/sheets/columns/`,
    { params },
  );
  return data;
}

/**
 * Import contacts from a Google Spreadsheet.
 * POST /api/v1/google-workspace/companies/{companyId}/sheets/import/
 * @param {{ companyId: string|number, payload: { spreadsheet_id: string, sheet_name?: string } }} args
 * @returns {Promise<{
 *   total_records: number,
 *   imported_count: number,
 *   created_count: number,
 *   updated_count: number,
 *   failed_count: number,
 *   errors: Array<{ row: number, name: string, phone: string, email: string, error: string }>
 * }>}
 */
/**
 * Import WhatsApp clients from a Google spreadsheet.
 * POST /api/v1/google-workspace/companies/{companyId}/clients/import/
 */
export async function importGoogleWorkspaceClients({ companyId, payload }) {
  const { data } = await apiClient.post(
    `/v1/google-workspace/companies/${companyId}/clients/import/`,
    payload,
  );
  return data;
}

export async function importGoogleWorkspaceSheet({ companyId, payload }) {
  const { data } = await apiClient.post(
    `/v1/google-workspace/companies/${companyId}/sheets/import/`,
    payload,
  );
  return data;
}

/**
 * Export contacts to a Google Spreadsheet (create new or append/write to existing).
 * POST /api/v1/google-workspace/companies/{companyId}/sheets/export/
 * @param {{ companyId: string|number, payload: { title?: string, spreadsheet_id?: string, sheet_name?: string } }} args
 * @returns {Promise<{
 *   spreadsheet_id: string,
 *   spreadsheet_url: string,
 *   title: string,
 *   row_count: number
 * }>}
 */
export async function exportGoogleWorkspaceSheet({
  companyId,
  payload,
  params,
}) {
  const queryParams = {
    ...(payload?.export_type ? { export_type: payload.export_type } : {}),
    ...(payload?.filter_params || {}),
    ...(params || {}),
  };
  const { data } = await apiClient.post(
    `/v1/google-workspace/companies/${companyId}/sheets/export/`,
    payload,
    { params: Object.keys(queryParams).length > 0 ? queryParams : undefined },
  );
  return data;
}

// ─── Google Drive On-Demand Upload Endpoints ──────────────────────────────────

/**
 * Upload/update a specific invoice PDF to Aexon/Invoices/ on Google Drive.
 * POST /api/v1/google-workspace/companies/{company_id}/drive/invoices/{invoice_id}/upload/
 * @param {{ companyId: string|number, invoiceId: string|number }} args
 * @returns {Promise<{ id: string, name: string, webViewLink: string, uploaded_at: string }>}
 */
export async function uploadInvoiceToDrive({ companyId, invoiceId }) {
  const { data } = await apiClient.post(
    `/v1/google-workspace/companies/${companyId}/drive/invoices/${invoiceId}/upload/`,
    {},
  );
  return data;
}

/**
 * Upload/update a specific quotation PDF to Aexon/Quotations/ on Google Drive.
 * POST /api/v1/google-workspace/companies/{company_id}/drive/quotations/{quotation_id}/upload/
 * Also accessible via /api/v1/whatsapp/companies/{company_id}/quotations/{quotation_id}/upload-drive/
 * @param {{ companyId: string|number, quotationId: string|number }} args
 * @returns {Promise<{ id: string, name: string, webViewLink: string, uploaded_at: string }>}
 */
export async function uploadQuotationToDrive({ companyId, quotationId }) {
  const { data } = await apiClient.post(
    `/v1/google-workspace/companies/${companyId}/drive/quotations/${quotationId}/upload/`,
    {},
  );
  return data;
}

/**
 * Upload/update a specific contact attachment to Aexon/Leads/{contact_id}/Attachments/.
 * POST /api/v1/google-workspace/companies/{company_id}/drive/attachments/{attachment_id}/upload/
 * Also accessible via /api/v1/leads/companies/{company_id}/contacts/{contact_id}/attachments/{attachment_id}/upload-drive/
 * @param {{ companyId: string|number, attachmentId: string|number }} args
 * @returns {Promise<{ id: string, name: string, webViewLink: string, uploaded_at: string }>}
 */
export async function uploadAttachmentToDrive({ companyId, attachmentId }) {
  const { data } = await apiClient.post(
    `/v1/google-workspace/companies/${companyId}/drive/attachments/${attachmentId}/upload/`,
    {},
  );
  return data;
}

/**
 * Standardized error message formatter for Google Drive upload responses.
 * Handles disconnected workspace (400), not found (404), generation errors, and custom details.
 */
export function formatDriveUploadError(
  err,
  fallback = 'Failed to upload to Google Drive.',
) {
  const code = err?.response?.data?.code;
  const detail =
    err?.response?.data?.detail ||
    err?.response?.data?.message ||
    parseApiError?.(err)?.message;

  if (
    code === 'google_workspace_disconnected' ||
    detail?.toLowerCase?.().includes('google workspace is not connected')
  ) {
    return 'Google Workspace is not connected. Please connect Google Workspace in Settings to enable Drive sync.';
  }

  if (err?.response?.status === 404) {
    return detail || 'Record not found in this company.';
  }

  return detail || fallback;
}

/**
 * Set Google Workspace Sync Preference and start backfill
 * POST /v1/integrations/google-workspace/companies/{companyId}/gmail/sync/
 */
export async function setGoogleWorkspaceSyncPreference({
  companyId,
  preference,
}) {
  const mode = preference === 'full_sync' ? 'all' : 'new_only';
  const { data } = await apiClient.post(
    `/v1/google-workspace/companies/${companyId}/gmail/sync/`,
    { mode },
  );
  return data;
}

// ─── Gmail Templates API ──────────────────────────────────────────────────────

/**
 * Retrieve all active Gmail/Email templates for a company.
 * GET /api/v1/google-workspace/companies/{companyId}/gmail/templates/
 */
export async function getGmailTemplates(companyId, params = {}) {
  const { data } = await apiClient.get(
    `/v1/google-workspace/companies/${companyId}/gmail/templates/`,
    { params },
  );
  return data;
}

/**
 * Create a new Gmail/Email template for a company.
 * POST /api/v1/google-workspace/companies/{companyId}/gmail/templates/
 * @param {{ companyId: string|number, payload: { name: string, subject: string, body: string, design_json: Array, category: string } }} args
 */
export async function createGmailTemplate({ companyId, payload }) {
  const { data } = await apiClient.post(
    `/v1/google-workspace/companies/${companyId}/gmail/templates/`,
    payload,
  );
  return data;
}

/**
 * Update an existing Gmail/Email template (partial update).
 * PUT /api/v1/google-workspace/companies/{companyId}/gmail/templates/{templateId}/
 * @param {{ companyId: string|number, templateId: number, payload: object }} args
 */
export async function updateGmailTemplate({ companyId, templateId, payload }) {
  const { data } = await apiClient.put(
    `/v1/google-workspace/companies/${companyId}/gmail/templates/${templateId}/`,
    payload,
  );
  return data;
}

/**
 * Soft-delete a Gmail/Email template (sets is_active=False on backend).
 * DELETE /api/v1/google-workspace/companies/{companyId}/gmail/templates/{templateId}/
 */
export async function deleteGmailTemplate({ companyId, templateId }) {
  const { data } = await apiClient.delete(
    `/v1/google-workspace/companies/${companyId}/gmail/templates/${templateId}/`,
  );
  return data;
}

// ─── Gmail Marketplace Templates API ──────────────────────────────────────────

/**
 * Retrieve active industries for marketplace.
 */
export async function getMarketplaceIndustries(params = {}) {
  try {
    const { data } = await apiClient.get(
      '/v1/google-workspace/marketplace/industries/',
      { params },
    );
    return data;
  } catch {
    const { data } = await apiClient.get(
      '/v1/whatsapp/marketplace/industries/',
      { params },
    );
    return data;
  }
}

/**
 * Retrieve active marketplace email templates for a company.
 * GET /api/v1/google-workspace/companies/{companyId}/marketplace/templates/
 */
export async function getMarketplaceEmailTemplates(companyId, params = {}) {
  try {
    const { data } = await apiClient.get(
      `/v1/google-workspace/companies/${companyId}/marketplace/templates/`,
      { params },
    );
    return data;
  } catch (err) {
    if (err?.response?.status === 404) {
      const { data } = await apiClient.get(
        `/v1/google-workspace/companies/${companyId}/marketplace-templates/`,
        { params },
      );
      return data;
    }
    throw err;
  }
}

/**
 * Retrieve single marketplace email template detail.
 * GET /api/v1/google-workspace/companies/{companyId}/marketplace/templates/{templateId}/
 */
export async function getMarketplaceEmailTemplateDetail(companyId, templateId) {
  try {
    const { data } = await apiClient.get(
      `/v1/google-workspace/companies/${companyId}/marketplace/templates/${templateId}/`,
    );
    return data;
  } catch (err) {
    if (err?.response?.status === 404) {
      const { data } = await apiClient.get(
        `/v1/google-workspace/companies/${companyId}/marketplace-templates/${templateId}/`,
      );
      return data;
    }
    throw err;
  }
}

// ─── Gmail Bulk Campaigns API ─────────────────────────────────────────────────

/**
 * List all Gmail bulk campaigns for a company.
 * GET /api/v1/google-workspace/companies/{companyId}/gmail/campaigns/
 */
/**
 * List all Gmail bulk campaigns for a company.
 * GET /api/v1/google-workspace/companies/{companyId}/gmail/campaigns/
 */
export async function getGmailCampaigns(companyId, params = {}) {
  const { data } = await apiClient.get(
    `/v1/google-workspace/companies/${companyId}/gmail/campaigns/`,
    { params },
  );
  return data;
}

/**
 * Retrieve details of a single Gmail bulk campaign (including recipient logs).
 * GET /api/v1/google-workspace/companies/{companyId}/gmail/campaigns/{campaignId}/
 */
export async function getGmailCampaignDetails({
  companyId,
  campaignId,
  params = {},
}) {
  const { data } = await apiClient.get(
    `/v1/google-workspace/companies/${companyId}/gmail/campaigns/${campaignId}/`,
    { params },
  );
  return data;
}

/**
 * Create and immediately send (or schedule) a Gmail bulk campaign.
 * POST /api/v1/google-workspace/companies/{companyId}/gmail/campaigns/
 * @param {{ companyId, payload: { campaign_name, template_id?, subject, body, recipients: [{email, name?, variables?}], scheduled_at? } }} args
 */
export async function createGmailCampaign({ companyId, payload }) {
  const { data } = await apiClient.post(
    `/v1/google-workspace/companies/${companyId}/gmail/campaigns/`,
    payload,
  );
  return data;
}

// ─── Email Follow-Up Automation APIs ─────────────────────────────────

export async function getEmailFollowUps(companyId) {
  const { data } = await apiClient.get(
    `/v1/google-workspace/companies/${companyId}/follow-ups/`,
  );
  return data;
}

export async function createEmailFollowUp(companyId, payload) {
  const { data } = await apiClient.post(
    `/v1/google-workspace/companies/${companyId}/follow-ups/`,
    payload,
  );
  return data;
}

export async function updateEmailFollowUp(companyId, id, payload) {
  const { data } = await apiClient.put(
    `/v1/google-workspace/companies/${companyId}/follow-ups/${id}/`,
    payload,
  );
  return data;
}

export async function deleteEmailFollowUp(companyId, id) {
  const { data } = await apiClient.delete(
    `/v1/google-workspace/companies/${companyId}/follow-ups/${id}/`,
  );
  return data;
}

export async function toggleEmailFollowUp(companyId, id) {
  const { data } = await apiClient.post(
    `/v1/google-workspace/companies/${companyId}/follow-ups/${id}/toggle/`,
  );
  return data;
}

export async function getEmailFollowUpRuns(companyId, params = {}) {
  const { data } = await apiClient.get(
    `/v1/google-workspace/companies/${companyId}/follow-up-runs/`,
    { params },
  );
  return data;
}

export async function stopEmailFollowUpRun(companyId, runId) {
  const { data } = await apiClient.post(
    `/v1/google-workspace/companies/${companyId}/follow-up-runs/${runId}/stop/`,
  );
  return data;
}

export async function skipEmailFollowUpRun(companyId, runId) {
  const { data } = await apiClient.post(
    `/v1/google-workspace/companies/${companyId}/follow-up-runs/${runId}/skip/`,
  );
  return data;
}

export async function enrollThreadFollowUp(companyId, threadId, sequenceId) {
  const { data } = await apiClient.post(
    `/v1/google-workspace/companies/${companyId}/gmail/threads/${threadId}/enroll-followup/`,
    { sequence_id: sequenceId },
  );
  return data;
}

export async function getThreadFollowUpStatus(companyId, threadId) {
  const { data } = await apiClient.get(
    `/v1/google-workspace/companies/${companyId}/gmail/threads/${threadId}/followup-status/`,
  );
  return data;
}

/**
 * Other Email (IMAP/SMTP) API integration methods
 */
export async function getOtherEmailProviders(companyId, email = '') {
  const { data } = await apiClient.get(
    `/v1/google-workspace/companies/${companyId}/other-email/providers/`,
    { params: email ? { email } : {} },
  );
  return data;
}

export async function testOtherEmailImap({ companyId, payload }) {
  const { data } = await apiClient.post(
    `/v1/google-workspace/companies/${companyId}/other-email/test-imap/`,
    payload,
  );
  return data;
}

export async function testOtherEmailSmtp({ companyId, payload }) {
  const { data } = await apiClient.post(
    `/v1/google-workspace/companies/${companyId}/other-email/test-smtp/`,
    payload,
  );
  return data;
}

export async function sendOtherEmailTestEmail({ companyId, toEmail }) {
  const { data } = await apiClient.post(
    `/v1/google-workspace/companies/${companyId}/other-email/send-test-email/`,
    { to_email: toEmail },
  );
  return data;
}

export async function getOtherEmailConnection(companyId) {
  const { data } = await apiClient.get(
    `/v1/google-workspace/companies/${companyId}/other-email/connection/`,
  );
  return data;
}

export async function connectOtherEmail({ companyId, payload }) {
  const { data } = await apiClient.post(
    `/v1/google-workspace/companies/${companyId}/other-email/connection/`,
    payload,
  );
  return data;
}

export async function disconnectOtherEmail(companyId) {
  const { data } = await apiClient.post(
    `/v1/google-workspace/companies/${companyId}/other-email/disconnect/`,
  );
  return data;
}

export async function syncOtherEmail(companyId) {
  const { data } = await apiClient.post(
    `/v1/google-workspace/companies/${companyId}/other-email/sync/`,
  );
  return data;
}
